import { z } from 'zod';
import {
  AuditSchema,
  HandlingClassSchema,
  MoneySchema,
  PartyIdSchema,
  PlainDateSchema,
  ProductIdSchema,
  QuantitySchema,
  TenantScopedSchema,
  UnitModeSchema,
  UnitSchema,
} from './primitives.js';

/* ------------------------------------------------------------------ *
 * Traits, not columns.
 *
 * "Perishable", "sold by catch weight", "has an origin to trace" are
 * true of tomatoes and false of a crate of napkins — and a business
 * selling neither still needs the same Product table. So a product
 * carries a `traits` object whose keys are owned by the trait pack.
 * The UI asks `product.traits.perishable?.shelfLifeDays`; if the pack
 * is off, the key is absent and the column never renders.
 * ------------------------------------------------------------------ */

export const PerishableTraitSchema = z.object({
  shelfLifeDays: z.number().int().positive(),
  /** Warn when the remaining life at delivery drops below this. */
  minRemainingDays: z.number().int().min(0).default(1),
  storageTempC: z.tuple([z.number(), z.number()]).nullable().default(null),
});

export const CatchWeightTraitSchema = z.object({
  /** Ordered as pieces, invoiced on the actual weighed amount. */
  nominalWeight: QuantitySchema,
  /** Acceptable deviation before the picker must flag it. */
  tolerancePct: z.number().min(0).max(100).default(10),
});

export const OriginTraitSchema = z.object({
  country: z.string().length(2),
  region: z.string().nullable().default(null),
  producerPartyId: PartyIdSchema.nullable().default(null),
  /** PDO / PGI / organic cert reference printed on the delivery note. */
  certification: z.string().nullable().default(null),
});

export const GradingTraitSchema = z.object({
  /** Calibre / size grade as printed on the crate: "58/62", "M", "Extra". */
  calibre: z.string().nullable().default(null),
  qualityClass: z.enum(['extra', 'i', 'ii']).nullable().default(null),
});

export const PackagingTraitSchema = z.object({
  /** Units of the sales unit in one purchase package. */
  unitsPerPackage: z.number().positive().default(1),
  packageLabel: z.string().default('box'),
  /** Deposit on returnable crates, refunded on the next delivery. */
  returnableDeposit: MoneySchema.nullable().default(null),
});

export const ProductTraitsSchema = z.object({
  perishable: PerishableTraitSchema.optional(),
  catchWeight: CatchWeightTraitSchema.optional(),
  origin: OriginTraitSchema.optional(),
  grading: GradingTraitSchema.optional(),
  packaging: PackagingTraitSchema.optional(),
});
export type ProductTraits = z.infer<typeof ProductTraitsSchema>;

/* ------------------------------------------------------------------ *
 * Category tree — a flat table with a parent pointer and a
 * materialised path. Sorting a tree in SQL every request is how
 * catalog screens get slow.
 * ------------------------------------------------------------------ */

export const CategorySchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: z.string().min(1),
  parentId: z.string().nullable().default(null),
  /** '/produce/vegetables/tomato' — makes descendant queries a prefix match. */
  path: z.string().min(1),
  /** Translated name per locale; falls back to the business default locale. */
  name: z.record(z.string(), z.string()),
  sortOrder: z.number().int().default(0),
  handlingClass: HandlingClassSchema.nullable().default(null),
});
export type Category = z.infer<typeof CategorySchema>;

/* ------------------------------------------------------------------ *
 * Product
 * ------------------------------------------------------------------ */

export const ProductSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: ProductIdSchema,
  /** Internal code, printed on crate labels and searched in the palette. */
  sku: z.string().min(1),
  barcode: z.string().nullable().default(null),
  /** Names are per-locale from day one. Retrofitting this is a rewrite. */
  name: z.record(z.string(), z.string()),
  description: z.record(z.string(), z.string()).default({}),
  categoryId: z.string().nullable().default(null),

  /** How this is sold. Drives every quantity input in the app. */
  unitMode: UnitModeSchema,
  salesUnit: UnitSchema,
  /** Minimum order and step, in the sales unit. 0.5 kg steps for fish, 1 pc for crates. */
  minQuantity: z.number().positive().default(1),
  quantityStep: z.number().positive().default(1),

  handlingClass: HandlingClassSchema,
  taxRateKey: z.string().min(1),
  traits: ProductTraitsSchema.default({}),

  /** Default supplier used when a shopping list is generated from orders. */
  defaultSupplierId: PartyIdSchema.nullable().default(null),
  /** Products a picker may substitute when this one is short. Ordered by preference. */
  substituteIds: z.array(ProductIdSchema).default([]),

  imageUrl: z.string().url().nullable().default(null),
  tags: z.array(z.string()).default([]),
  status: z.enum(['active', 'seasonal-off', 'discontinued', 'draft']).default('draft'),
});
export type Product = z.infer<typeof ProductSchema>;

/* ------------------------------------------------------------------ *
 * Pricing
 *
 * Four layers, resolved in order. The resolution function is here in
 * shared so the price a salesperson sees, the price the API charges
 * and the price on the invoice can never diverge.
 *
 *   1. base price          (the catalog)
 *   2. price list          (segment: "hotels", "market stall")
 *   3. personal price      (this customer, this product)
 *   4. today's market cost (buying side; a floor for the margin guard)
 * ------------------------------------------------------------------ */

export const PriceListSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: z.string().min(1),
  name: z.string().min(1),
  /** Applied to base price when no explicit entry exists. -500 = 5% off. */
  defaultAdjustmentBps: z.number().int().default(0),
  validFrom: PlainDateSchema.nullable().default(null),
  validTo: PlainDateSchema.nullable().default(null),
});
export type PriceList = z.infer<typeof PriceListSchema>;

export const PriceEntrySchema = z.object({
  productId: ProductIdSchema,
  /** Fixed price wins over adjustment when both are present. */
  price: MoneySchema.nullable().default(null),
  adjustmentBps: z.number().int().nullable().default(null),
  /** Volume break: this entry applies from this quantity up. */
  fromQuantity: z.number().positive().default(1),
});
export type PriceEntry = z.infer<typeof PriceEntrySchema>;

export const PersonalPriceSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: z.string().min(1),
  partyId: PartyIdSchema,
  productId: ProductIdSchema,
  price: MoneySchema,
  validFrom: PlainDateSchema,
  validTo: PlainDateSchema.nullable().default(null),
  /** Why this customer pays less. Shown on hover; saves an argument later. */
  reason: z.string().nullable().default(null),
});
export type PersonalPrice = z.infer<typeof PersonalPriceSchema>;

/**
 * The morning quote. Market prices move daily; this is the record of
 * what a product cost and was sold for on a given trading day, which
 * is what the price board renders and what margin analytics reads.
 */
export const MarketQuoteSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: z.string().min(1),
  productId: ProductIdSchema,
  tradingDay: PlainDateSchema,
  supplierId: PartyIdSchema.nullable().default(null),
  /** What we pay today. */
  cost: MoneySchema,
  /** What we sell at today, before price lists and personal prices. */
  sell: MoneySchema,
  /** Yesterday's sell price, denormalised so the board can strike it through. */
  previousSell: MoneySchema.nullable().default(null),
  availability: z.enum(['plenty', 'tight', 'scarce', 'none']).default('plenty'),
  note: z.string().nullable().default(null),
});
export type MarketQuote = z.infer<typeof MarketQuoteSchema>;

/* --- Resolution ----------------------------------------------------- */

export type PriceSource = 'personal' | 'price-list' | 'market' | 'base';

export interface ResolvedPrice {
  price: import('./primitives.js').Money;
  source: PriceSource;
  /** Populated when the price came from a list or personal agreement. */
  reason: string | null;
}

export interface PriceResolutionInput {
  basePrice: import('./primitives.js').Money;
  quantity: number;
  marketQuote?: MarketQuote | undefined;
  priceList?: { list: PriceList; entries: PriceEntry[] } | undefined;
  personalPrice?: PersonalPrice | undefined;
  today: string;
}

const applyBps = (
  m: import('./primitives.js').Money,
  bps: number,
): import('./primitives.js').Money => ({
  minor: Math.round(m.minor * (1 + bps / 10_000)),
  currency: m.currency,
});

const isLive = (from: string | null, to: string | null, today: string): boolean =>
  (from === null || from <= today) && (to === null || to >= today);

export const resolvePrice = (input: PriceResolutionInput): ResolvedPrice => {
  const { personalPrice, priceList, marketQuote, basePrice, quantity, today } = input;

  if (personalPrice && isLive(personalPrice.validFrom, personalPrice.validTo, today)) {
    return { price: personalPrice.price, source: 'personal', reason: personalPrice.reason };
  }

  const listBase = marketQuote ? marketQuote.sell : basePrice;

  if (priceList && isLive(priceList.list.validFrom, priceList.list.validTo, today)) {
    const entry = priceList.entries
      .filter((e) => e.fromQuantity <= quantity)
      .sort((a, b) => b.fromQuantity - a.fromQuantity)[0];

    if (entry?.price) {
      return { price: entry.price, source: 'price-list', reason: priceList.list.name };
    }
    const bps = entry?.adjustmentBps ?? priceList.list.defaultAdjustmentBps;
    if (bps !== 0) {
      return { price: applyBps(listBase, bps), source: 'price-list', reason: priceList.list.name };
    }
  }

  if (marketQuote) {
    return { price: marketQuote.sell, source: 'market', reason: marketQuote.tradingDay };
  }
  return { price: basePrice, source: 'base', reason: null };
};

/** Margin in basis points against today's cost. Negative means selling at a loss. */
export const marginBps = (
  sell: import('./primitives.js').Money,
  cost: import('./primitives.js').Money,
): number | null => {
  if (sell.currency !== cost.currency || sell.minor === 0) return null;
  return Math.round(((sell.minor - cost.minor) / sell.minor) * 10_000);
};
